import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { useTranslation } from "react-i18next";
import { LimitIndicator } from "@/components/ui/limit-indicator";
import Header from "@/components/layout/Header";
import SEOHead from "@/components/seo/SEOHead";
import DashboardNav from "@/components/dashboard/DashboardNav";
import StaticToolBackground from "@/components/layout/StaticToolBackground";
import OpportunityStack from "@/components/dashboard/OpportunityStack";
import OpportunityDetail from "@/components/dashboard/OpportunityDetail";
import LimitReachedState from "@/components/dashboard/LimitReachedState";
import ProviderProfile from "@/components/dashboard/ProviderProfile";
import PaymentSetup from "@/components/dashboard/PaymentSetup";
import NotificationDropdown from "@/components/operator/NotificationDropdown";
import { useAuth } from "@/hooks/useAuth";
import { useProvider } from "@/hooks/useProvider";
import { useOpportunities } from "@/hooks/useOpportunities";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Tab = "new" | "in_review" | "claimed" | "completed" | "declined" | "profile" | "payment";

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { profile, isLoadingProfile, hasCompletedOnboarding } = useProvider();
  const {
    newOpportunities,
    inReviewOpportunities,
    claimedOpportunities,
    completedOpportunities,
    declinedOpportunities,
    isLoading: oppsLoading,
    acceptOpportunity,
    declineOpportunity,
    askQuestion,
    completeOpportunity,
    submitClaim,
  } = useOpportunities();

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>("new");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Deep-link: select opportunity from URL ?id=...
  useEffect(() => {
    const paramId = searchParams.get("id");
    if (!paramId || oppsLoading) return;
    const allOpps = [...newOpportunities, ...inReviewOpportunities, ...claimedOpportunities, ...completedOpportunities, ...declinedOpportunities];
    const found = allOpps.find(o => o.id === paramId);
    if (found) {
      if (newOpportunities.some(o => o.id === paramId)) setActiveTab("new");
      else if (inReviewOpportunities.some(o => o.id === paramId)) setActiveTab("in_review");
      else if (claimedOpportunities.some(o => o.id === paramId)) setActiveTab("claimed");
      else if (completedOpportunities.some(o => o.id === paramId)) setActiveTab("completed");
      else if (declinedOpportunities.some(o => o.id === paramId)) setActiveTab("declined");
      setSelectedId(paramId);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, oppsLoading, newOpportunities, inReviewOpportunities, claimedOpportunities, completedOpportunities, declinedOpportunities]);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (isLoadingProfile || !user) return;
    // Only redirect to onboarding if profile exists but onboarding not completed
    if (profile && !hasCompletedOnboarding) {
      navigate("/onboarding");
    }
  }, [profile, isLoadingProfile, hasCompletedOnboarding, navigate, user]);

  const opportunities = activeTab === "new"
    ? newOpportunities
    : activeTab === "in_review"
      ? inReviewOpportunities
      : activeTab === "claimed"
        ? claimedOpportunities.map(o => ({
            ...o,
            status: o.claim?.status === "APPROVED" ? "claim_approved" as const
              : o.claim?.status === "REJECTED" ? "claim_rejected" as const
              : "claimed" as const,
          }))
        : activeTab === "completed"
          ? completedOpportunities
          : activeTab === "declined"
            ? declinedOpportunities
            : [];

  const selectedOpp = opportunities.find(o => o.id === selectedId);
  const isPaymentMissing = profile && !profile.has_payment_method;
  const isLimitReached = profile && profile.weekly_lead_used >= profile.weekly_lead_limit;

  const handleAccept = async () => {
    if (!selectedOpp) return;
    if (isPaymentMissing) {
      toast.error(t("dashboard.paymentRequired", "Bitte hinterlegen Sie zuerst ein Zahlungsmittel."));
      setActiveTab("payment");
      return;
    }
    if (isLimitReached) { toast.error(t("dashboard.weeklyLimitReached")); return; }
    try {
      await acceptOpportunity.mutateAsync(selectedOpp.id);
      toast.success(t("dashboard.accepted"));
      setSelectedId(null);
    } catch (error: any) {
      const msg = error?.message || "";
      if (msg.includes("PAYMENT_METHOD_REQUIRED")) {
        toast.error(t("dashboard.paymentRequired"));
        setActiveTab("payment");
        return;
      }
      if (msg.includes("SUBSCRIPTION_BLOCKED")) {
        toast.error(t("dashboard.subscriptionBlocked"));
        return;
      }
      if (msg.includes("ACCEPTANCE_QUOTA_EXHAUSTED")) {
        toast.error(t("dashboard.quotaExhausted"));
        return;
      }
      toast.error(t("dashboard.acceptError"));
    }
  };

  const handleDecline = async () => {
    if (!selectedOpp) return;
    try {
      await declineOpportunity.mutateAsync(selectedOpp.id);
      toast.success(t("dashboard.declined"));
      setSelectedId(null);
    } catch { toast.error(t("dashboard.declineError")); }
  };

  const handleAskQuestion = async (question: string) => {
    if (!selectedOpp) return;
    await askQuestion.mutateAsync({ opportunityId: selectedOpp.id, question });
    toast.success(t("dashboard.questionSent"));
  };

  const handleComplete = async (outcome: "won" | "lost" | "no_fit") => {
    if (!selectedOpp) return;
    try {
      await completeOpportunity.mutateAsync({ opportunityId: selectedOpp.id, outcome });
      toast.success(t("dashboard.completed"));
      setSelectedId(null);
    } catch { toast.error(t("dashboard.completeError")); }
  };

  const handleClaim = async (reasonCode: string, reasonText: string) => {
    if (!selectedOpp) return;
    try {
      await submitClaim.mutateAsync({ opportunityId: selectedOpp.id, reasonCode, reasonText });
      toast.success(t("dashboard.claimSuccess", "Reklamation eingereicht"));
    } catch (err: any) {
      toast.error(err.message || t("dashboard.claimError", "Fehler beim Einreichen"));
    }
  };

  if (authLoading || isLoadingProfile || oppsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-caption">{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <StaticToolBackground />
      <SEOHead title={t("dashboard.title")} noindex canonical="/dashboard" />
      <Header />

      <main id="main-content" className="pt-24 pb-12">
        <div className="section-padding border-b border-border">
          <div className="max-w-6xl mx-auto py-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div className="space-y-1">
                <h1 className="text-headline">{t("dashboard.title")}</h1>
                <p className="text-caption">{t("dashboard.subtitle")}</p>
              </div>
              {profile && (
                <div className="flex items-center gap-6">
                  <NotificationDropdown />
                  <LimitIndicator used={profile.monthly_opportunities_used} total={profile.monthly_opportunity_limit} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="section-padding">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
              <div className="lg:col-span-3">
                <div className="sticky top-28">
                  <DashboardNav
                    activeTab={activeTab}
                    onTabChange={(tab) => { setActiveTab(tab); setSelectedId(null); }}
                    counts={{
                      new: newOpportunities.length,
                      in_review: inReviewOpportunities.length,
                      claimed: claimedOpportunities.length,
                      completed: completedOpportunities.length,
                      declined: declinedOpportunities.length,
                    }}
                    showPaymentWarning={!!isPaymentMissing}
                  />
                  {isPaymentMissing && (
                    <div className="mt-4 p-4 bg-destructive/5 border border-destructive/20">
                      <p className="text-xs text-destructive font-medium">{t("dashboard.paymentMissing")}</p>
                      <p className="text-xs text-destructive/80 mt-1">{t("dashboard.paymentMissingDesc")}</p>
                      <button onClick={() => setActiveTab("payment")} className="text-xs text-destructive underline mt-2">
                        {t("dashboard.setupPayment")}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-5">
                {activeTab === "payment" ? (
                  <PaymentSetup />
                ) : activeTab === "profile" ? (
                  <ProviderProfile />
                ) : activeTab === "new" && isLimitReached ? (
                  <LimitReachedState />
                ) : (
                  <OpportunityStack
                    opportunities={opportunities as any}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    emptyMessage={
                      activeTab === "new" ? t("dashboard.noNew")
                        : activeTab === "in_review" ? t("dashboard.noInReview")
                        : activeTab === "claimed" ? t("dashboard.noClaimed", "Keine reklamierten Anfragen")
                        : activeTab === "declined" ? t("dashboard.noDeclined", "Keine abgelehnten Anfragen")
                        : t("dashboard.noCompleted")
                    }
                  />
                )}
              </div>

              <div className="lg:col-span-4">
                {activeTab !== "profile" && activeTab !== "payment" && (
                  selectedOpp ? (
                    <OpportunityDetail
                      opportunity={selectedOpp as any}
                      onAccept={handleAccept}
                      onDecline={handleDecline}
                      
                      onComplete={handleComplete}
                      onClaim={handleClaim}
                      isAccepting={acceptOpportunity.isPending}
                      isDeclining={declineOpportunity.isPending}
                      isClaiming={submitClaim.isPending}
                    />
                  ) : (
                    <div className="h-full min-h-[400px] flex items-center justify-center border border-dashed border-border p-12">
                      <p className="text-caption text-center">{t("dashboard.selectForDetails")}</p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
